import { GP_VIEW_PERMISSION } from '@supply-mover-2-0/shared';
import type { ListBuilderColumn } from '../../types';

/**
 * Creates the standard column layout for invoice lists
 */
export const createInvoiceColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'invoice-number',
		fields: [
			{
				id: 'invoice-external-id-field',
				link: {
					type: 'object',
					enabled: true,
					idField: '_id',
					objectTable: 'invoice',
				},
				dataConfig: {
					type: 'conditional',
					conditions: [
						{
							field: 'externalId2',
							operator: 'not_equals',
							value: null,
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId2', objectTable: 'invoice' },
							},
						},
					],
					defaultDataConfig: {
						type: 'field',
						field: { fieldName: 'externalId', objectTable: 'invoice' },
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
			{
				id: 'invoice-line-break-ids',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'invoice-internal-id-field',
				link: {
					type: 'object',
					enabled: true,
					idField: '_id',
					objectTable: 'invoice',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: '_id', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [
			{ id: 'h1-external', text: 'External ID' },
			{ id: 'h1-internal', text: 'Internal ID' },
		],
		minWidth: 120,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'ASC', fieldName: 'externalId', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'client-info',
		fields: [
			{
				id: 'client-name-field',
				link: {
					type: 'object',
					enabled: true,
					idField: '_fk_client__id',
					objectTable: 'client',
				},
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'clientName',
						objectTable: 'invoice',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-1',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'client-address-field',
				dataConfig: {
					type: 'template',
					template: '{clientAddress1} {clientAddress2}',
					templateFields: [],
				},
				styleConfig: {
					// 'sm' (12px), not 'xxs': SIZE_MAP has no xxs entry, so
					// xxs silently fell back to md (14px) and the address
					// rendered BIGGER than everything around it.
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
			{
				id: 'line-break-2',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'client-city-state-field',
				dataConfig: {
					type: 'template',
					template: '{clientCity}, {clientState} {clientZip}',
					templateFields: [],
				},
				styleConfig: {
					// 'sm' for the same SIZE_MAP-fallback reason as above.
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h2', text: 'Client' }],
		minWidth: 180,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'ASC', fieldName: 'clientName', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'top',
	},
	{
		id: 'date',
		fields: [
			{
				id: 'date-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'date', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
					formatOptions: { dateFormat: 'short' },
				},
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h3', text: 'Date' }],
		minWidth: 80,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{ direction: 'DESC', fieldName: 'date', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'salesperson',
		fields: [
			{
				id: 'salesperson-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'salesRepFirstName', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-salesperson', text: 'Sales Rep' }],
		minWidth: 100,
		sortable: false,
		alignment: 'left',
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'lines',
		fields: [
			{
				id: 'lines-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'numberOfLines', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'default' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h4', text: 'Lines' }],
		minWidth: 60,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{ direction: 'DESC', fieldName: 'numberOfLines', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'adjustments',
		fields: [
			{
				id: 'tax-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'tax', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textPrimary' },
				},
				displayStyle: 'currency',
			},
			{
				id: 'line-break-tax',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'freight-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'freight', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'currency',
			},
			{
				id: 'line-break-freight',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'discount-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'discount', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'currency',
			},
		],
		headers: [
			{ id: 'h-tax', text: 'Tax' },
			{ id: 'h-freight', text: 'Freight' },
			{ id: 'h-discount', text: 'Discount' },
		],
		minWidth: 90,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{ direction: 'DESC', fieldName: 'tax', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'top',
	},
	{
		id: 'invoice-totals',
		fields: [
			{
				id: 'subtotal-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'subtotal', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textPrimary' },
				},
				displayStyle: 'currency',
			},
			{
				id: 'line-break-5',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'total-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'total', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'md',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'currency',
			},
			{
				id: 'line-break-6',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'line_break',
			},
			{
				id: 'amount-owed-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'm_sm_payment_outstandingBalance',
						objectTable: 'invoice',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textSecondary' },
				},
				displayStyle: 'currency',
			},
		],
		headers: [
			{ id: 'h6-subtotal', text: 'Subtotal' },
			{ id: 'h6-total', text: 'Total' },
			{ id: 'h6-amount-owed', text: 'Amount Owed' },
		],
		minWidth: 120,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{ direction: 'DESC', fieldName: 'total', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'gp-totals',
		fields: [
			{
				id: 'total-gp-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'totalGP', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'textPrimary' },
				},
				displayStyle: 'currency',
				// Field-level only: keeps GP out of CSV exports. FieldRenderer
				// renders a lock icon in-cell so the header stays visible.
				requiredPermission: GP_VIEW_PERMISSION,
			},
		],
		headers: [{ id: 'h8-total-gp', text: 'GP' }],
		minWidth: 120,
		sortable: true,
		alignment: 'right',
		sortFields: [
			{ direction: 'DESC', fieldName: 'totalGP', objectTable: 'invoice' },
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'top',
	},
	{
		id: 'payment-status',
		fields: [
			{
				id: 'payment-status-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'paymentStatus', objectTable: 'invoice' },
				},
				styleConfig: {
					size: 'xs',
					valueMapping: [
						{
							value: 'Outstanding',
							displayText: 'Outstanding',
							color: { type: 'warning' },
						},
						{
							value: 'Paid in Full',
							displayText: 'Paid in Full',
							color: { type: 'success' },
						},
					],
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h7', text: 'Payment Status' }],
		minWidth: 120,
		sortable: false,
		alignment: 'center',
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'center',
	},
];
